import React, { ReactNode, useCallback, useEffect, useLayoutEffect } from 'react'
import classNames from 'classnames'

import { CustomSizeType, SizeKeysMD, SizeKeysXL, SizeTypes } from '../../ui/Types/SizeType'
import { dispatchCloseModalEvent, dispatchOpenModalEvent } from '../../utils/cookie.utils'

import styles from './Modal.module.scss'

interface HasCloseModal {
  closeModal(): void
}

interface Props extends HasCloseModal {
  size?: SizeType
  cardClassName?: string
  pullDown?: boolean
  render: (props: HasCloseModal) => ReactNode
}

type SizeType = CustomSizeType<SizeTypes, SizeKeysMD | SizeKeysXL>

const useModalEffect = (props: Props) => {
  const { closeModal } = props

  useLayoutEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    document.documentElement.classList.add('is-clipped')
    dispatchOpenModalEvent()

    return () => {
      dispatchCloseModalEvent()
      document.documentElement.classList.remove('is-clipped')
    }
  }, [])

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal()
      }
    },
    [closeModal]
  )

  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [handleKeyUp])
}

const useModalHandlers = (props: Props) => {
  const { closeModal } = props

  const handleBackgroundMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    closeModal()
  }

  const handleContentClick = (event: React.MouseEvent) => {
    event.stopPropagation()
  }

  return { handleBackgroundMouseDown, handleContentClick }
}

/**
 * @deprecated This component is deprecated in favor of the {@link ui/v2/Modal} component
 * */
export const Modal: React.FC<Props> = (props) => {
  const { size, cardClassName, pullDown = false } = props
  const { closeModal, render } = props

  useModalEffect(props)
  const { handleContentClick, handleBackgroundMouseDown } = useModalHandlers(props)

  return (
    <div
      id='modal'
      className={classNames('modal', 'is-active', styles.modalContainer, {
        [`is-${size}`]: size,
        [styles.pullDown]: pullDown,
      })}
    >
      <div
        className={styles.modalOverlay}
        onClick={handleBackgroundMouseDown}
        onMouseDown={handleBackgroundMouseDown}
      />
      <div
        className={classNames(cardClassName, 'modal-card', styles.modalCard)}
        onMouseDown={handleContentClick}
      >
        <div className={classNames('modalWrapper', styles.modalWrapper)}>
          {render({ closeModal })}
        </div>
      </div>
    </div>
  )
}
